/**
 * Stickers: who may create them, validation, search/ranking, recents, "My stickers" pack order, the picker sections
 * and animation sniffing. Pure; shared by the API (enforcement) and every client (affordances), with golden vectors.
 */
import type { MediaSettings, Member, Sticker, StickerPack, StickerPackScope, StickerRef } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { fitWithin } from "./files.ts";

/** Static stickers are re-encoded on the client to this square size (WebP). */
export const STICKER_SIZE = 512;
/** Largest sticker image (animated originals are uploaded as they are; static ones end up far smaller). */
export const MAX_STICKER_BYTES = 1_048_576;
/** Largest edge of an animated original (they aren't resized). */
export const MAX_ANIMATED_EDGE = 1024;
/** Image types a sticker may be. */
export const STICKER_MIMES: string[] = ["image/webp", "image/png", "image/apng", "image/gif"];
export const MAX_STICKER_NAME = 40;
export const MAX_PACK_NAME = 50;
export const MAX_KEYWORDS = 10;
export const MAX_KEYWORD_LENGTH = 24;
export const MAX_STICKERS_PER_PACK = 120;
export const MAX_RECENT_STICKERS = 24;
export const MAX_MY_PACKS = 100;
/** Biggest a sticker is drawn in a message (it has no bubble). */
export const MESSAGE_STICKER_SIZE = 160;
/** Recently used stickers shown first in the picker. */
export const RECENT_SECTION_ID = "recent";
export const RESULTS_SECTION_ID = "results";

export const DEFAULT_MEDIA_SETTINGS: MediaSettings = { stickerCreators: "everyone", gifContentFilter: "high", gifsEnabled: true };

// ---------- Permissions ----------

function restricted(m: Member): boolean {
  return m.access !== undefined ? m.access.level === "restricted" : m.role === "guest";
}

function active(m: Member, now: number): boolean {
  if (m.deactivated) return false;
  const exp = m.access === undefined ? undefined : m.access.expiresAt;
  return exp === undefined || exp > now;
}

/** Owners and admins with full access curate workspace packs and change the media settings. */
export function canManageStickers(member: Member, now: number): boolean {
  return active(member, now) && !restricted(member) && (member.role === "owner" || member.role === "admin");
}

/**
 * May create stickers and packs. Restricted members (guests, agents) only with `can.createStickers`; full members
 * per the workspace setting ("admins" → owners and admins only).
 */
export function canCreateStickers(member: Member, settings: MediaSettings, now: number): boolean {
  if (!active(member, now)) return false;
  if (restricted(member)) return member.access !== undefined && member.access.can.createStickers === true;
  if (settings.stickerCreators === "everyone") return true;
  return member.role === "owner" || member.role === "admin";
}

/** May add stickers to, rename or delete a pack: its creator for personal packs, managers for workspace packs. */
export function canEditPack(pack: { scope: StickerPackScope; createdBy: string }, member: Member, settings: MediaSettings, now: number): boolean {
  if (pack.scope === "workspace") return canManageStickers(member, now);
  return pack.createdBy === member.userId && canCreateStickers(member, settings, now);
}

/** May delete a pack: whoever may edit it, and managers for any pack (moderation). */
export function canDeletePack(pack: { scope: StickerPackScope; createdBy: string }, member: Member, settings: MediaSettings, now: number): boolean {
  return canManageStickers(member, now) || canEditPack(pack, member, settings, now);
}

// ---------- Validation ----------

/** "Party, cats  🐈, PARTY" → ["party", "cats", "🐈"]: split on commas/whitespace, lower-case, deduped, capped. */
export function normalizeKeywords(raw: string): string[] {
  const out: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const k = part.trim().toLowerCase().slice(0, MAX_KEYWORD_LENGTH);
    if (k === "" || out.includes(k)) continue;
    out.push(k);
    if (out.length === MAX_KEYWORDS) break;
  }
  return out;
}

export function stickerNameProblem(name: string): MessageDescriptor | null {
  const n = name.trim();
  if (n === "") return msg("stickers.create.nameRequired");
  if (n.length > MAX_STICKER_NAME) return msg("stickers.create.nameTooLong", { max: MAX_STICKER_NAME });
  return null;
}

export function packNameProblem(name: string): MessageDescriptor | null {
  const n = name.trim();
  if (n === "") return msg("stickers.pack.nameRequired");
  if (n.length > MAX_PACK_NAME) return msg("stickers.pack.nameTooLong", { max: MAX_PACK_NAME });
  return null;
}

/** Why an image can't be a sticker as it is (after the client's processing), or null. */
export function stickerFileProblem(file: { mime: string; size: number; width?: number; height?: number }, animated: boolean): MessageDescriptor | null {
  const mime = file.mime.split(";")[0]!.trim().toLowerCase();
  if (!STICKER_MIMES.includes(mime)) return msg("stickers.create.badType");
  if (file.size > MAX_STICKER_BYTES) return msg("stickers.create.tooBig", { max: "1 MB" });
  if (animated && ((file.width ?? 0) > MAX_ANIMATED_EDGE || (file.height ?? 0) > MAX_ANIMATED_EDGE)) {
    return msg("stickers.create.tooLarge", { max: MAX_ANIMATED_EDGE });
  }
  return null;
}

// ---------- Search ----------

/** Lower-case without diacritics ("Café" → "cafe"), for matching. */
export function foldText(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/**
 * Relevance of a sticker for a (folded) query, 0 = no match: exact name 100, name prefix 80, exact keyword 70,
 * keyword prefix 60, a word of the name starts with it 50, name contains it 30.
 */
export function stickerScore(sticker: { name: string; keywords: string[] }, query: string): number {
  const q = foldText(query);
  if (q === "") return 0;
  const name = foldText(sticker.name);
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  const keywords = sticker.keywords.map(foldText);
  if (keywords.includes(q)) return 70;
  if (keywords.some((k) => k.startsWith(q))) return 60;
  if (name.split(/[\s_-]+/).some((w) => w.startsWith(q))) return 50;
  if (name.includes(q)) return 30;
  return 0;
}

/**
 * Stickers matching `query`, best first. Ties keep pack order (packs as listed in `packOrder`, then the rest in
 * `packs` order) and the order inside the pack. Each sticker once.
 */
export function searchStickers(packs: StickerPack[], packOrder: string[], query: string, limit: number): Sticker[] {
  const ordered = orderedPacks(packs, packOrder);
  const scored: { s: Sticker; score: number; i: number }[] = [];
  const seen = new Set<string>();
  let i = 0;
  for (const p of ordered) {
    for (const s of p.stickers) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      const score = stickerScore(s, query);
      if (score > 0) scored.push({ s, score, i });
      i += 1;
    }
  }
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.slice(0, limit).map((x) => x.s);
}

/** `packOrder` packs first (in that order), then every other pack in `packs` order. */
function orderedPacks(packs: StickerPack[], packOrder: string[]): StickerPack[] {
  const byId = new Map(packs.map((p) => [p.id, p]));
  const out: StickerPack[] = [];
  for (const id of packOrder) {
    const p = byId.get(id);
    if (p !== undefined && !out.includes(p)) out.push(p);
  }
  for (const p of packs) if (!out.includes(p)) out.push(p);
  return out;
}

// ---------- Recents ----------

/** Moves `id` to the front (most recent first), at most `max` entries. */
export function pushRecent(recent: string[], id: string, max: number): string[] {
  return [id, ...recent.filter((x) => x !== id)].slice(0, Math.max(0, max));
}

/** The recent stickers that still exist, most recent first. */
export function recentStickers(recent: string[], packs: StickerPack[]): Sticker[] {
  const byId = new Map<string, Sticker>();
  for (const p of packs) for (const s of p.stickers) byId.set(s.id, s);
  const out: Sticker[] = [];
  for (const id of recent) {
    const s = byId.get(id);
    if (s !== undefined) out.push(s);
  }
  return out;
}

// ---------- My stickers (pack order) ----------

/** My packs in my order (ids of packs that are gone are skipped). */
export function myPacks(packs: StickerPack[], myPackIds: string[]): StickerPack[] {
  const byId = new Map(packs.map((p) => [p.id, p]));
  const out: StickerPack[] = [];
  for (const id of myPackIds) {
    const p = byId.get(id);
    if (p !== undefined && !out.includes(p)) out.push(p);
  }
  return out;
}

/** Adds a pack at the end (no-op when it's already there). */
export function addPack(myPackIds: string[], packId: string): string[] {
  if (myPackIds.includes(packId) || myPackIds.length >= MAX_MY_PACKS) return myPackIds;
  return [...myPackIds, packId];
}

export function removePack(myPackIds: string[], packId: string): string[] {
  return myPackIds.includes(packId) ? myPackIds.filter((x) => x !== packId) : myPackIds;
}

/** Moves a pack to `toIndex` (clamped) in my list; unknown ids leave it unchanged. */
export function movePack(myPackIds: string[], packId: string, toIndex: number): string[] {
  const from = myPackIds.indexOf(packId);
  if (from < 0) return myPackIds;
  const rest = myPackIds.filter((x) => x !== packId);
  const at = Math.max(0, Math.min(rest.length, Math.trunc(toIndex)));
  if (at === from) return myPackIds;
  return [...rest.slice(0, at), packId, ...rest.slice(at)];
}

/** "My stickers" for someone who never arranged it: every workspace pack (oldest first). */
export function defaultMyPacks(packs: StickerPack[]): string[] {
  return packs
    .filter((p) => p.scope === "workspace")
    .toSorted((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((p) => p.id)
    .slice(0, MAX_MY_PACKS);
}

/** One row of the "Add stickers" browser. */
export type BrowseEntry = { pack: StickerPack; added: boolean; mine: boolean };

/** Workspace packs (by name), then my personal packs, then other people's packs I added. */
function byName(a: StickerPack, b: StickerPack): number {
  return foldText(a.name).localeCompare(foldText(b.name)) || (a.id < b.id ? -1 : 1);
}

export function browsePacks(packs: StickerPack[], myPackIds: string[], meId: string): BrowseEntry[] {
  const rank = (p: StickerPack) => (p.scope === "workspace" ? 0 : p.createdBy === meId ? 1 : 2);
  return packs
    .filter((p) => p.scope === "workspace" || p.createdBy === meId || myPackIds.includes(p.id))
    .toSorted((a, b) => rank(a) - rank(b) || byName(a, b))
    .map((pack) => ({ pack, added: myPackIds.includes(pack.id), mine: pack.createdBy === meId }));
}

/** The sticker shown for a pack (its chosen icon, else its first sticker). */
export function packIcon(pack: StickerPack): Sticker | undefined {
  const chosen = pack.iconStickerId === undefined ? undefined : pack.stickers.find((s) => s.id === pack.iconStickerId);
  return chosen ?? pack.stickers[0];
}

/** What a message carries for a sticker. */
export function stickerRef(sticker: Sticker): StickerRef {
  const r: StickerRef = {
    id: sticker.id,
    packId: sticker.packId,
    name: sticker.name,
    url: sticker.url,
    mime: sticker.mime,
    width: sticker.width,
    height: sticker.height,
  };
  if (sticker.animated === true) r.animated = true;
  return r;
}

/** Size of a sticker in a message (no bubble, at most MESSAGE_STICKER_SIZE). */
export function stickerDisplaySize(width: number, height: number): { width: number; height: number } {
  return fitWithin(width, height, MESSAGE_STICKER_SIZE, MESSAGE_STICKER_SIZE);
}

// ---------- Picker sections ----------

export type StickerSection = {
  /** RECENT_SECTION_ID, RESULTS_SECTION_ID or a pack id. */
  id: string;
  /** Translated title for recents/results; packs use `name`. */
  title?: MessageDescriptor;
  name?: string;
  packId?: string;
  stickers: Sticker[];
};

/**
 * The Stickers tab. Without a query: recents, then each of my packs. With a query: one "results" section searched over
 * every pack I can see (mine first).
 */
export function pickerSections(packs: StickerPack[], myPackIds: string[], recent: string[], query: string, limit: number): StickerSection[] {
  if (foldText(query) !== "") {
    return [{ id: RESULTS_SECTION_ID, title: msg("stickers.picker.results"), stickers: searchStickers(packs, myPackIds, query, limit) }];
  }
  const out: StickerSection[] = [];
  const recents = recentStickers(recent, packs);
  if (recents.length > 0) out.push({ id: RECENT_SECTION_ID, title: msg("stickers.picker.recent"), stickers: recents });
  for (const p of myPacks(packs, myPackIds)) {
    if (p.stickers.length === 0) continue;
    out.push({ id: p.id, name: p.name, packId: p.id, stickers: p.stickers });
  }
  return out;
}

// ---------- Animation sniffing ----------

/**
 * True for animated images: a GIF with 2+ frames, a PNG with an `acTL` chunk (APNG) before its image data, or a WebP
 * whose VP8X header has the animation flag (or that has an ANIM chunk). `bytes` = the file's leading bytes (the whole
 * file for GIFs, whose second frame can come late).
 */
export function detectAnimation(bytes: number[]): boolean {
  const at = (i: number) => (i < bytes.length ? bytes[i]! : -1);
  const ascii = (i: number, n: number) => String.fromCharCode(...bytes.slice(i, i + n));
  if (ascii(0, 6) === "GIF87a" || ascii(0, 6) === "GIF89a") return gifFrames(bytes) > 1;
  if (at(0) === 0x89 && ascii(1, 3) === "PNG") {
    let p = 8;
    while (p + 8 <= bytes.length) {
      const len = ((at(p) << 24) >>> 0) + (at(p + 1) << 16) + (at(p + 2) << 8) + at(p + 3);
      const type = ascii(p + 4, 4);
      if (type === "acTL") return true;
      if (type === "IDAT" || type === "IEND") return false;
      p += 12 + len;
    }
    return false;
  }
  if (ascii(0, 4) === "RIFF" && ascii(8, 4) === "WEBP") {
    if (ascii(12, 4) === "VP8X") return (at(20) & 0x02) !== 0;
    let p = 12;
    while (p + 8 <= bytes.length) {
      const type = ascii(p, 4);
      if (type === "ANIM" || type === "ANMF") return true;
      const len = at(p + 4) + (at(p + 5) << 8) + (at(p + 6) << 16) + ((at(p + 7) << 24) >>> 0);
      p += 8 + len + (len % 2);
    }
  }
  return false;
}

/** Number of image descriptors in a GIF (stops at the trailer, at the end of the bytes, or at 2). */
function gifFrames(b: number[]): number {
  if (b.length < 13) return 0;
  let p = 13;
  if ((b[10]! & 0x80) !== 0) p += 3 * 2 ** ((b[10]! & 0x07) + 1);
  let frames = 0;
  const skipSubBlocks = () => {
    while (p < b.length) {
      const size = b[p]!;
      p += 1;
      if (size === 0) return;
      p += size;
    }
  };
  while (p < b.length && frames < 2) {
    const block = b[p]!;
    if (block === 0x3b) break;
    if (block === 0x21) {
      p += 2;
      skipSubBlocks();
    } else if (block === 0x2c) {
      frames += 1;
      if (p + 10 > b.length) break;
      const packed = b[p + 9]!;
      p += 10;
      if ((packed & 0x80) !== 0) p += 3 * 2 ** ((packed & 0x07) + 1);
      p += 1;
      skipSubBlocks();
    } else {
      break;
    }
  }
  return frames;
}
